// src/utils/ouvertureFnct.ts — SCE Issues
// Ouvrir une FNC-T en quatre étapes (refonte de l'ouverture, étapes 4 et 5 —
// 14/10, docs/chantier-fnct.md § 13). Pur, testable.
// Maquette : https://claude.ai/artifact/FzkN1t8sj1DJ8Gh3cjE5vJ.
//
// Deux cas, une seule fenêtre :
//  - P&ID / PPL (`pid`) : étape 1 = les écarts, d'où se déduisent description,
//    documents impactés et les trois actions habituelles. D'un RELEVÉ (étape 4 :
//    la FNC-T existe, son N° est pris), ou d'un problème dont la machine n'a pas
//    de NVS lisible (pas de relevé possible : écarts saisis à l'étape 1) ;
//  - cas général (`general`, étape 5) : étape 1 = le constat — catégorie
//    proposée, ce qui ne va pas, documents et tests impactés (rapport client, en
//    anglais), justification de la priorité (interne, facultative).
// Puis 2. le contexte (tout proposé), 3. les actions, 4. relire comme le client.
// Rien n'est écrit avant « Ouvrir la FNC-T ». D'un relevé, le N° ne change pas ;
// d'un problème, il est pris à l'ouverture (le plus grand de l'année + 1).

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import type { Equipement, Issue } from '@/types/issue.types'
import { CATEGORIE_PROCESS, LIEUX_FNCT, PRIORITES_FNCT, RESPONSABLES_FNCT, libelleCategorie } from '@/config/fnct.config'
import {
  actionsFnct,
  actionsModele,
  SUPPORTS_ECART,
  categorieProposee,
  expertPropose,
  leaderPropose,
  preremplirSaisie,
  textesModele,
  typeProduitDe,
  type ActionSaisie,
  type EcartPidPpl,
} from './escaladeFnct'
import { ajouterJoursOuvres, formatNumero } from './fnct'
import { ajouterLigneEcart, lireEcarts, type EcartReleve } from './releveEcarts'
import { nomCourt } from './actionsFnct'

export type EtapeOuverture = 1 | 2 | 3 | 4
export type CasOuverture = 'pid' | 'general'

export const TITRES_ETAPES: Record<CasOuverture, Record<EtapeOuverture, string>> = {
  pid: { 1: 'Les écarts', 2: 'Le contexte', 3: 'Les actions', 4: 'Relire et ouvrir' },
  general: { 1: 'Le constat', 2: 'Le contexte', 3: 'Les actions', 4: 'Relire et ouvrir' },
}

export interface SaisieOuverture {
  cas: CasOuverture
  /** Écarts du relevé retirés dans la fenêtre (par leur id). */
  retires: string[]
  /** Écarts ajoutés dans la fenêtre (écrits à l'ouverture seulement). */
  ajoutes: EcartReleve[]
  // ── Le constat (cas général) ──
  /** Problem category (valeur de la base) ; Process d'office pour un écart P&ID / PPL. */
  categorie: string
  /** Ce qui ne va pas, en anglais (rapport client). */
  description: string
  /** Documents et tests impactés, en anglais (rapport client). */
  documentsImpactes: string
  /** Justification de la priorité : interne, facultative (Priority explanation de la base). */
  justification: string
  // ── Le contexte ──
  numerosSerie: string[]
  lieuDetection: string
  dateDetection: Jour | null
  priorite: string
  leader: string
  leaderUPN: string
  /** Leader encore proposé : il suit le lieu de détection. */
  leaderAuto: boolean
  expert: string
  /** Adresse de l'expert choisi (non écrite : pas de colonne) ; montre la bonne personne. */
  expertUPN?: string
  /** Expert encore proposé : il suit la catégorie. */
  expertAuto: boolean
  // ── Les actions ──
  /** Qui met à jour le P&ID et la PPL (cas P&ID / PPL). */
  conceptionProcess: string
  /** null tant qu'on n'est pas passé à l'étape 3 : elles se proposent alors. */
  actions: ActionSaisie[] | null
  /** D'une machine : le problème lié, facultatif (référencé à l'ouverture, hors essai). */
  problemeLie?: string
}

/** D'un relevé : tout ce qui peut être proposé l'est ; on vérifie, on ne remplit pas. */
export function preremplirOuverture(p: { releve: Fnct; chefProjet: string }): SaisieOuverture {
  const lieu = p.releve.lieuDetection || 'I-Test'
  return {
    cas: 'pid',
    retires: [],
    ajoutes: [],
    categorie: CATEGORIE_PROCESS,
    description: '',
    documentsImpactes: '',
    justification: p.releve.explicationPriorite ?? '',
    numerosSerie: [...p.releve.numerosSerie],
    lieuDetection: lieu,
    // Le jour du premier écart : celui de l'enregistrement du relevé.
    dateDetection: p.releve.dateDetection ?? p.releve.enregistrement,
    priorite: p.releve.priorite || 'Medium',
    ...(p.releve.leader ? { leader: p.releve.leader, leaderUPN: p.releve.leaderUPN ?? '' } : leaderPropose(lieu, p.chefProjet)),
    leaderAuto: !p.releve.leader,
    expert: p.releve.expert || expertPropose(CATEGORIE_PROCESS),
    expertAuto: !p.releve.expert,
    conceptionProcess: RESPONSABLES_FNCT.conceptionProcess[0].nom,
    actions: null,
  }
}

/**
 * D'un problème (étape 5) : la FNC-T n'existe pas encore. `base` porte ce que la
 * fenêtre ne saisit pas (modèle, exécution, CVE, enregistrée par) ; la saisie, ce
 * qui est proposé : la machine du problème seule (1 FNC-T = 1 équipement, le plus
 * souvent), détection en test le jour de la déclaration, priorité d'après la
 * criticité, catégorie d'après le métier et la famille.
 */
export function preremplirDepuisProbleme(p: {
  issue: Issue
  equipement: Equipement | null | undefined
  enregistrePar: string
  cas: CasOuverture
  aujourdhui: Jour
}): { base: Fnct; saisie: SaisieOuverture } {
  const s = preremplirSaisie({ issue: p.issue, soeurs: [], equipement: p.equipement, enregistrePar: p.enregistrePar })
  return nouvelleFnct({
    ...s,
    cas: p.cas,
    categorie: p.cas === 'pid' ? CATEGORIE_PROCESS : categorieProposee(p.issue),
    enregistrePar: p.enregistrePar,
    aujourdhui: p.aujourdhui,
  })
}

/**
 * D'une MACHINE, sans problème (fiche équipement, bouton central, /fnct — 15/10,
 * « machine d'abord ») : la machine choisie, détection en test aujourd'hui,
 * priorité moyenne, leader proposé ; la catégorie est à choisir (cas général),
 * Process d'office pour un écart P&ID / PPL.
 */
export function preremplirDepuisMachine(p: {
  equipement: Pick<Equipement, 'numeroSerie' | 'familleEquipement' | 'designation' | 'cve' | 'chefProjet'>
  enregistrePar: string
  cas: CasOuverture
  aujourdhui: Jour
}): { base: Fnct; saisie: SaisieOuverture } {
  const lieu = 'I-Test'
  return nouvelleFnct({
    cas: p.cas,
    categorie: p.cas === 'pid' ? CATEGORIE_PROCESS : '',
    enregistrePar: p.enregistrePar,
    aujourdhui: p.aujourdhui,
    modele: p.equipement.familleEquipement ?? '',
    typeProduit: typeProduitDe(p.equipement.designation),
    numerosSerie: [p.equipement.numeroSerie.trim().toUpperCase()],
    cve: (p.equipement.cve ?? '').trim(),
    lieuDetection: lieu,
    dateDetection: p.aujourdhui,
    priorite: 'Medium',
    ...leaderPropose(lieu, p.equipement.chefProjet ?? ''),
  })
}

/** La FNC-T pas encore créée (`base`) et la saisie proposée, d'un problème ou d'une machine. */
function nouvelleFnct(p: {
  cas: CasOuverture
  categorie: string
  enregistrePar: string
  aujourdhui: Jour
  modele: string
  typeProduit: string
  numerosSerie: string[]
  cve: string
  lieuDetection: string
  dateDetection: Jour | null
  priorite: string
  leader: string
  leaderUPN: string
}): { base: Fnct; saisie: SaisieOuverture } {
  const base: Fnct = {
    numero: '',
    annee: Number(p.aujourdhui.slice(0, 4)),
    rang: 0,
    enregistrement: p.aujourdhui,
    enregistrePar: p.enregistrePar,
    leader: '',
    expert: '',
    modele: p.modele,
    typeProduit: p.typeProduit,
    numerosSerie: p.numerosSerie,
    cve: p.cve,
    categorie: p.categorie,
    documentsImpactes: '',
    description: '',
    lieuDetection: p.lieuDetection,
    dateDetection: p.dateDetection,
    priorite: p.priorite,
    explicationPriorite: '',
    severite: null,
    frequence: null,
    detectabilite: null,
    analyseRisque: '',
    clotureCorrection: null,
    pretInvestigation: null,
    clotureInvestigation: null,
    numeroCapa: '',
    commentaires: '',
    legitimite: '',
    cloture: null,
    ecarts: '',
    ouverture: null,
  }
  return {
    base,
    saisie: {
      cas: p.cas,
      retires: [],
      ajoutes: [],
      categorie: p.categorie,
      description: '',
      documentsImpactes: '',
      justification: '',
      numerosSerie: p.numerosSerie,
      lieuDetection: p.lieuDetection,
      dateDetection: p.dateDetection,
      priorite: p.priorite,
      leader: p.leader,
      leaderUPN: p.leaderUPN,
      leaderAuto: true,
      expert: expertPropose(p.categorie),
      expertAuto: true,
      conceptionProcess: RESPONSABLES_FNCT.conceptionProcess[0].nom,
      actions: null,
    },
  }
}

/** Le lieu change : le leader encore proposé suit (test → coordinateur tests process, sinon le chef de projet). */
export function changerLieu(s: SaisieOuverture, lieu: string, chefProjet: string): SaisieOuverture {
  return { ...s, lieuDetection: lieu, ...(s.leaderAuto ? leaderPropose(lieu, chefProjet) : {}) }
}

/** La catégorie change : l'expert encore proposé suit. */
export function changerCategorie(s: SaisieOuverture, categorie: string): SaisieOuverture {
  return { ...s, categorie, ...(s.expertAuto ? { expert: expertPropose(categorie), expertUPN: '' } : {}) }
}

// ─── Les écarts (cas P&ID / PPL) ───

/** Les écarts qui entreront dans la FNC-T : ceux de la colonne (lisibles), moins les retirés, plus les ajoutés. */
export function ecartsOuverture(colonne: string | null | undefined, s: Pick<SaisieOuverture, 'retires' | 'ajoutes'>): EcartReleve[] {
  const retires = new Set(s.retires)
  return [
    ...lireEcarts(colonne).flatMap((l) => (l.ecart && !retires.has(l.ecart.id) ? [l.ecart] : [])),
    ...s.ajoutes,
  ]
}

/**
 * La colonne Ecarts écrite à l'ouverture : les lignes de la colonne RELUE, moins
 * les écarts retirés (par leur id), plus les ajoutés en fin. Une ligne illisible
 * reste telle quelle ; un écart relevé par un autre entre-temps aussi.
 */
export function colonneOuverture(colonne: string | null | undefined, s: Pick<SaisieOuverture, 'retires' | 'ajoutes'>): string {
  const retires = new Set(s.retires)
  const gardees = lireEcarts(colonne)
    .filter((l) => !(l.ecart && retires.has(l.ecart.id)))
    .map((l) => l.brute)
    .join('\n')
  return s.ajoutes.reduce((col, e) => ajouterLigneEcart(col, e), gardees)
}

function pourModele(ecarts: readonly EcartReleve[]): EcartPidPpl[] {
  return ecarts.map((e) => ({ support: e.support, nature: e.nature, texte: e.repere }))
}

/** Description (une phrase par écart) et documents impactés (tests compris) : déduits des écarts. */
export function textesOuverture(ecarts: readonly EcartReleve[]): { description: string; documentsImpactes: string } {
  const t = textesModele(pourModele(ecarts))
  return { description: t.description, documentsImpactes: t.documentsImpactes }
}

/** Les textes du rapport : déduits des écarts (P&ID / PPL) ou écrits au constat (cas général). */
export function textesFinaux(s: SaisieOuverture, ecarts: readonly EcartReleve[]): { description: string; documentsImpactes: string } {
  return s.cas === 'pid' ? textesOuverture(ecarts) : { description: s.description.trim(), documentsImpactes: s.documentsImpactes.trim() }
}

/**
 * Justification de la priorité d'un écart P&ID / PPL : STANDARDISÉE (Guillaume,
 * 14/10), comme dans la base (« Test 75255 is not compliant », « Tests 12893 and
 * 13593 aren't compliants ») — les tests non conformes, déduits des écarts.
 */
export function justificationPidPpl(ecarts: readonly Pick<EcartReleve, 'support'>[]): string {
  const tests = [...new Set(ecarts.map((e) => SUPPORTS_ECART.find((s) => s.valeur === e.support)?.test ?? ''))].filter(Boolean).sort()
  if (tests.length === 0) return ''
  if (tests.length === 1) return `Test ${tests[0]} is not compliant.`
  return `Tests ${tests.slice(0, -1).join(', ')} and ${tests.at(-1)} are not compliant.`
}

/** La justification écrite : standard (P&ID / PPL) ou celle du constat (cas général, facultative). */
export function justificationFinale(s: Pick<SaisieOuverture, 'cas' | 'justification'>, ecarts: readonly Pick<EcartReleve, 'support'>[]): string {
  return s.cas === 'pid' ? justificationPidPpl(ecarts) : s.justification.trim()
}

// ─── Les actions ───

/** P&ID / PPL : les trois habituelles (mise à jour, documentation, retest par qui ouvre). Cas général : aucune, des suggestions. */
export function actionsInitiales(
  s: Pick<SaisieOuverture, 'cas' | 'conceptionProcess'>,
  ecarts: readonly EcartReleve[],
  ouvrePar: string,
  aujourdhui: Jour,
): ActionSaisie[] {
  return s.cas === 'pid' ? actionsModele(pourModele(ecarts), s.conceptionProcess, ouvrePar, aujourdhui) : []
}

/**
 * Échéance à N semaines OUVRÉES (5 jours ouvrés chacune, fériés sautés) : les
 * pastilles « 1-4 sem. » parlent la même langue que les délais par défaut
 * (BE : 10 j ouvrés = « 2 sem. »).
 */
export function echeanceSemaines(aujourdhui: Jour, semaines: number): Jour {
  return ajouterJoursOuvres(aujourdhui, semaines * 5)
}

/** L'échéance tombe-t-elle pile à N semaines ouvrées (1 à 8) ? La pastille est alors marquée. */
export function semainesDe(aujourdhui: Jour, echeance: Jour | null): number | null {
  if (!echeance) return null
  for (let n = 1; n <= 8; n += 1) if (echeanceSemaines(aujourdhui, n) === echeance) return n
  return null
}

// ─── Ce qui manque, étape par étape ───

export function manquantsEtape(s: SaisieOuverture, etape: EtapeOuverture, ecarts: readonly EcartReleve[]): string[] {
  const m: string[] = []
  if (etape === 1) {
    if (s.cas === 'pid') {
      if (ecarts.length === 0) m.push('un écart')
    } else {
      if (!s.categorie) m.push('la catégorie')
      if (!s.description.trim()) m.push('ce qui ne va pas')
      if (!s.documentsImpactes.trim()) m.push('les documents et tests impactés')
    }
  }
  if (etape === 2) {
    if (s.numerosSerie.length === 0) m.push('un équipement')
    if (!s.dateDetection) m.push('la date de détection')
    if (!s.leader.trim()) m.push('le leader')
  }
  if (etape === 3) {
    const actions = s.actions ?? []
    if (actions.length === 0) m.push('une action')
    if (actions.some((a) => !a.description.trim() || !a.responsable.trim() || !a.echeance)) {
      m.push('la description, le responsable et l’échéance de chaque action')
    }
  }
  return m
}

/** Ce qui manque encore pour ouvrir (toutes étapes), avec l'étape où le corriger. */
export function manquantsOuverture(s: SaisieOuverture, ecarts: readonly EcartReleve[]): { etape: EtapeOuverture; manque: string }[] {
  return ([1, 2, 3] as const).flatMap((etape) => manquantsEtape(s, etape, ecarts).map((manque) => ({ etape, manque })))
}

// ─── Les résumés du rail des étapes ───

const pluriel = (n: number, mot: string): string => `${n} ${mot}${n > 1 ? 's' : ''}`

export function resumesEtapes(s: SaisieOuverture, ecarts: readonly EcartReleve[]): Record<EtapeOuverture, string> {
  const docs = new Set(ecarts.map((e) => (e.support === 'pid' ? 'P&ID' : 'PPL')))
  const docsFr = docs.size === 2 ? 'P&ID et PPL' : [...docs][0] ?? ''
  const actions = s.actions
  return {
    1: s.cas === 'pid' ? [pluriel(ecarts.length, 'écart'), docsFr].filter(Boolean).join(' · ') : s.categorie ? libelleCategorie(s.categorie) : 'Catégorie à choisir',
    2: [pluriel(s.numerosSerie.length, 'équipement'), s.leader ? `leader ${nomCourt(s.leader)}` : 'leader à choisir'].join(' · '),
    3: actions === null ? (s.cas === 'pid' ? 'Proposées d’après les écarts' : 'À poser') : pluriel(actions.length, 'action'),
    4: 'Rien n’est écrit avant',
  }
}

// ─── Ce qui est écrit ───

/**
 * La FNC-T après l'ouverture, construite sur la ligne RELUE (un champ que la
 * fenêtre ne touche pas reste tel quel) : Ouverture = aujourd'hui, écarts et
 * textes recalculés sur la colonne relue (P&ID / PPL) ou repris du constat.
 */
export function fnctApresOuverture(relue: Fnct, s: SaisieOuverture, aujourdhui: Jour): Fnct {
  const t = textesFinaux(s, ecartsOuverture(relue.ecarts, s))
  return {
    ...relue,
    ecarts: colonneOuverture(relue.ecarts, s),
    ouverture: aujourdhui,
    description: t.description,
    documentsImpactes: t.documentsImpactes,
    explicationPriorite: justificationFinale(s, ecartsOuverture(relue.ecarts, s)),
    categorie: s.cas === 'pid' ? CATEGORIE_PROCESS : s.categorie,
    numerosSerie: [...s.numerosSerie],
    lieuDetection: s.lieuDetection,
    dateDetection: s.dateDetection,
    priorite: s.priorite,
    leader: s.leader.trim(),
    leaderUPN: s.leaderUPN,
    expert: s.expert.trim(),
  }
}

/** D'un problème : la FNC-T créée à l'ouverture, avec le N° pris à cet instant. */
export function fnctNouvelle(base: Fnct, s: SaisieOuverture, annee: number, rang: number, aujourdhui: Jour): Fnct {
  return { ...fnctApresOuverture(base, s, aujourdhui), numero: formatNumero(annee, rang), annee, rang, enregistrement: aujourdhui }
}

/** Les actions à créer, numérotées à la suite de celles que la FNC-T aurait déjà. */
export function actionsOuverture(numero: string, s: SaisieOuverture, aujourdhui: Jour, deja: number): ActionFnct[] {
  return actionsFnct(numero, (s.actions ?? []).filter((a) => a.description.trim()), aujourdhui).map((a, i) => ({
    ...a,
    id: `${numero}-A${String(deja + i + 1).padStart(2, '0')}`,
  }))
}

/** Le texte de l'historique : « Ouverte avec 7 écarts et 3 actions ». */
export function texteOuverture(f: Fnct, actions: readonly ActionFnct[], nbEcarts: number): string {
  const premiere = nbEcarts > 0 ? `Ouverte avec ${pluriel(nbEcarts, 'écart')} et ${pluriel(actions.length, 'action')}` : `Ouverte avec ${pluriel(actions.length, 'action')}`
  return [premiere, f.description, ...actions.map((a) => `${a.description} — ${a.responsable}`)].join('\n')
}

/** « En ouvrant » (étape 4), en clair. `numero` : celui du relevé ; null, pris à l'ouverture. */
export function effetsOuverture(p: {
  numero: string | null
  ecarts: readonly EcartReleve[]
  saisie: SaisieOuverture
  problemes: readonly string[]
  enEssai: boolean
}): string[] {
  const actions = (p.saisie.actions ?? []).filter((a) => a.description.trim())
  const { problemes } = p
  return [
    p.numero
      ? `${p.numero} s’ouvre, avec son N° de relevé ; l’ancienneté se comptera à partir d’aujourd’hui.`
      : 'La FNC-T reçoit son N° à l’ouverture : le suivant de l’année.',
    p.saisie.cas === 'pid' ? `Les ${pluriel(p.ecarts.length, 'écart')} entrent dans la description du rapport client.` : '',
    `${pluriel(actions.length, 'action')}, chacune chez son responsable.`,
    problemes.length === 0
      ? ''
      : p.enEssai
        ? `Essai : ${problemes.length > 1 ? `les problèmes ${problemes.join(', ')} ne reçoivent` : `le problème ${problemes[0]} ne reçoit`} pas encore la référence de la FNC-T.`
        : `${problemes.length > 1 ? `Les problèmes ${problemes.join(', ')} reçoivent` : `Le problème ${problemes[0]} reçoit`} la référence de la FNC-T.`,
  ].filter(Boolean)
}

/** Libellés français du lieu et de la priorité (résumés). */
export function lieuCourt(v: string): string {
  return LIEUX_FNCT.find((l) => l.valeur === v)?.libelle ?? v
}
export function prioriteCourte(v: string): string {
  return PRIORITES_FNCT.find((p) => p.valeur === v)?.libelle ?? v
}

// ─── C'est ouvert (écran de fin) ───

/** « 7 écarts · 3 équipements · 3 actions ». */
export function resumeOuverte(f: Pick<Fnct, 'numerosSerie'>, nbActions: number, nbEcarts: number): string {
  return [nbEcarts > 0 ? pluriel(nbEcarts, 'écart') : '', pluriel(f.numerosSerie.length, 'équipement'), pluriel(nbActions, 'action')]
    .filter(Boolean)
    .join(' · ')
}

/** La prochaine étape : l'action à l'échéance la plus proche, qui, quoi, pour quand. */
export function prochaineApresOuverture(actions: readonly Pick<ActionFnct, 'responsable' | 'description' | 'echeance'>[]): string {
  const a = [...actions].filter((x) => x.echeance).sort((x, y) => (x.echeance ?? '').localeCompare(y.echeance ?? ''))[0] ?? actions[0]
  if (!a) return ''
  const [aaaa, mm, jj] = (a.echeance ?? '').split('-')
  return `${a.responsable} : « ${a.description} »${a.echeance ? `, pour le ${jj}/${mm}/${aaaa}` : ''}.`
}

/** Ce qui a été fait, en clair. `rates` : problèmes qu'on n'a pas pu référencer. */
export function faitsOuverture(p: { numero: string; nbEcarts: number; depuisReleve: boolean; problemes: readonly string[]; rates: readonly string[]; enEssai: boolean }): string[] {
  const ok = p.problemes.filter((id) => !p.rates.includes(id))
  return [
    p.depuisReleve && p.nbEcarts > 0 ? `Les ${pluriel(p.nbEcarts, 'écart')} du relevé sont dans la FNC-T.` : '',
    p.problemes.length === 0
      ? ''
      : p.enEssai
        ? `Essai : rien n’est écrit sur ${p.problemes.length > 1 ? 'les problèmes' : 'le problème'} ${p.problemes.join(', ')}.`
        : ok.length > 0
          ? `${ok.length > 1 ? `Les problèmes ${ok.join(', ')} portent` : `Le problème ${ok[0]} porte`} la référence ${p.numero}.`
          : '',
    p.rates.length > 0 ? `À référencer à la main : ${p.rates.join(', ')}.` : '',
    'Le rapport client peut être généré dès maintenant.',
  ].filter(Boolean)
}

// ─── Modifier un écart à l'étape 1 (14/10, contrôle face à la maquette) ───

type Ecarts = Pick<SaisieOuverture, 'retires' | 'ajoutes'>

/**
 * Modifier un écart : un écart ajouté dans la fenêtre est remplacé sur place ; un
 * écart du relevé est retiré et remplacé sous le MÊME id (auteur, date, problème et
 * photos gardés), donc réécrit à l'ouverture sans doublon.
 */
export function modifierEcart(s: Ecarts, original: EcartReleve, m: Pick<EcartReleve, 'support' | 'nature' | 'repere'>): Ecarts {
  const change = { support: m.support, nature: m.nature, repere: m.repere.trim() }
  if (s.ajoutes.some((e) => e.id === original.id)) {
    return { retires: s.retires, ajoutes: s.ajoutes.map((e) => (e.id === original.id ? { ...e, ...change } : e)) }
  }
  return { retires: [...s.retires, original.id], ajoutes: [...s.ajoutes, { ...original, ...change }] }
}

/** Retirer un écart : un ajouté disparaît ; un écart du relevé (modifié ou non) sort à l'ouverture. */
export function retirerEcart(s: Ecarts, id: string): Ecarts {
  const ajoute = s.ajoutes.some((e) => e.id === id)
  return {
    retires: ajoute || s.retires.includes(id) ? s.retires : [...s.retires, id],
    ajoutes: s.ajoutes.filter((e) => e.id !== id),
  }
}

/** Les écarts du relevé vraiment retirés (un écart modifié est remplacé, pas retiré). */
export function ecartsRetires(s: Ecarts): string[] {
  return s.retires.filter((id) => !s.ajoutes.some((e) => e.id === id))
}

/** « Les remettre » : les écarts retirés reviennent ; les modifiés restent modifiés. */
export function remettreEcarts(s: Ecarts): Ecarts {
  return { retires: s.retires.filter((id) => s.ajoutes.some((e) => e.id === id)), ajoutes: s.ajoutes }
}

/** Un écart du relevé modifié dans la fenêtre (et non ajouté) ? */
export function estModifie(s: Ecarts, id: string): boolean {
  return s.retires.includes(id) && s.ajoutes.some((e) => e.id === id)
}
